
import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Save, RefreshCw } from "lucide-react";
import { storiesData } from "@/data/stories";

// Import refactored components
import StoryList from "./stories/StoryList";
import StoryEditorDialog from "./stories/StoryEditorDialog";
import StoriesMetadataForm from "./stories/StoriesMetadataForm";
import { StoriesFormData } from "./stories/types";

const StoriesEditor: React.FC = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingStory, setEditingStory] = useState<any>(null);
  const [isStoryDialogOpen, setIsStoryDialogOpen] = useState(false);
  
  // Initialize the form with current content
  const form = useForm<StoriesFormData>({
    defaultValues: {
      title_en: "Success Stories",
      title_ar: "قصص النجاح",
      subtitle_en: "Discover inspiring stories of sustainable development projects across Palestine",
      subtitle_ar: "اكتشف قصصًا ملهمة لمشاريع التنمية المستدامة في جميع أنحاء فلسطين",
      readmore_en: "Read more",
      readmore_ar: "اقرأ المزيد",
      stories: storiesData.map(story => ({
        id: story.id,
        title_en: story.title.en,
        title_ar: story.title.ar,
        summary_en: story.summary.en,
        summary_ar: story.summary.ar,
        content_en: story.content.en,
        content_ar: story.content.ar,
        date: story.date,
        image: story.image || "/placeholder.svg",
        featured: story.featured,
        sdgIds: story.sdgIds,
        slug: story.slug
      }))
    },
  });
  
  // Filter stories based on search term
  const filteredStories = form.watch('stories').filter(story => 
    story.title_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
    story.title_ar.includes(searchTerm) ||
    story.summary_en.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleAddStory = () => {
    const newStory = {
      id: Math.max(0, ...form.getValues('stories').map(s => s.id)) + 1,
      title_en: "",
      title_ar: "",
      summary_en: "",
      summary_ar: "",
      content_en: "",
      content_ar: "",
      date: new Date().toISOString().substring(0, 10),
      image: "/placeholder.svg",
      featured: false,
      sdgIds: [],
      slug: `story-${Date.now()}`
    };
    
    setEditingStory(newStory);
    setIsStoryDialogOpen(true);
  };
  
  const handleEditStory = (story: any) => {
    setEditingStory(story);
    setIsStoryDialogOpen(true);
  };
  
  const handleSaveStory = (updatedStory: any) => {
    const currentStories = [...form.getValues('stories')];
    const existingIndex = currentStories.findIndex(s => s.id === updatedStory.id);
    
    if (existingIndex >= 0) {
      // Update existing story
      currentStories[existingIndex] = updatedStory;
    } else {
      // Add new story
      currentStories.push(updatedStory);
    }
    
    form.setValue('stories', currentStories);
    setIsStoryDialogOpen(false);
    
    toast({
      title: language === "en" ? "Story saved" : "تم حفظ القصة",
      description: language === "en" ? "The story has been saved successfully" : "تم حفظ القصة بنجاح",
    });
  };
  
  const handleDeleteStory = (storyId: number) => {
    const currentStories = [...form.getValues('stories')];
    form.setValue('stories', currentStories.filter(s => s.id !== storyId));
    
    toast({
      title: language === "en" ? "Story deleted" : "تم حذف القصة",
      description: language === "en" ? "The story has been deleted successfully" : "تم حذف القصة بنجاح",
    });
  };
  
  const onSubmit = (data: StoriesFormData) => {
    setIsSaving(true);
    
    // Simulate API call to save data
    setTimeout(() => {
      setIsSaving(false);
      toast({
        title: language === "en" ? "Changes saved" : "تم حفظ التغييرات",
        description: language === "en" ? "Stories section has been updated" : "تم تحديث قسم القصص",
      });
      console.log("Stories data saved:", data);
    }, 1000);
  };
  
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Metadata Forms */}
          <StoriesMetadataForm form={form} />
          
          {/* Stories Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {language === "en" ? "Manage Stories" : "إدارة القصص"}
              </CardTitle>
              <CardDescription>
                {language === "en" 
                  ? "Add, edit or remove stories that appear on the website" 
                  : "إضافة أو تعديل أو إزالة القصص التي تظهر على الموقع"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StoryList 
                stories={filteredStories}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onAddStory={handleAddStory}
                onEditStory={handleEditStory}
                onDeleteStory={handleDeleteStory}
              />
            </CardContent>
          </Card>
        
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => form.reset()}
              className="border-palsustain-green text-palsustain-green"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {language === "en" ? "Reset" : "إعادة تعيين"}
            </Button>
            <Button 
              type="submit" 
              className="bg-palsustain-green hover:bg-palsustain-green/90"
              disabled={isSaving}
            >
              <Save className="mr-2 h-4 w-4" />
              {isSaving 
                ? (language === "en" ? "Saving..." : "جاري الحفظ...") 
                : (language === "en" ? "Save Changes" : "حفظ التغييرات")
              }
            </Button>
          </div>
        </form>
      </Form>
      
      <StoryEditorDialog
        isOpen={isStoryDialogOpen}
        onOpenChange={setIsStoryDialogOpen}
        editingStory={editingStory}
        onSave={handleSaveStory}
      />
    </>
  );
};

export default StoriesEditor;
